import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import type { Order } from "types/delivery.types";
import { useAnimatedPosition } from "shared/hooks/tracking/useAnimatedPosition";
import { useTrackingMode } from "features/tracking/hooks/useTrackingMode";
import { bearingDegrees } from "shared/lib/tracking/geo";
import TrackingModeToggle from "features/tracking/components/TrackingModeToggle";
import SimpleRouteTracker from "features/tracking/components/SimpleRouteTracker";
import {
  MOCK_TRACKING_CLIENT_ANIMATION_MS,
  MOCK_TRACKING_CLIENT_INTERVAL_MS,
  MOCK_TRACKING_CLIENT_STEP,
  USE_MOCK_TRACKING_CLIENT,
} from "core/config/mockTracking";
import { lerpLatLng } from "shared/lib/tracking/interpolate";
import type { DriverLocationSnapshot } from "types/tracking.types";
import { useUIStore } from "core/store/uiStore";
import { resolveTrackingVehicleType } from "features/tracking/lib/vehicleMarker";

const LiveTrackingMap = lazy(
  () => import("features/tracking/components/LiveTrackingMap"),
);

interface OrderTrackingMapProps {
  order: Order;
}

function hasCoords(
  lat: number | null | undefined,
  lng: number | null | undefined,
): lat is number {
  return (
    lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng)
  );
}

function useMockLocation(
  order: Order,
  enabled: boolean,
): DriverLocationSnapshot | null {
  const [mock, setMock] = useState<DriverLocationSnapshot | null>(null);

  useEffect(() => {
    if (!enabled) {
      setMock(null);
      return;
    }
    if (
      !hasCoords(order.pickupLat, order.pickupLng) ||
      !hasCoords(order.deliveryLat, order.deliveryLng)
    ) {
      return;
    }
    const pickup = { lat: order.pickupLat!, lng: order.pickupLng! };
    const delivery = { lat: order.deliveryLat!, lng: order.deliveryLng! };
    let t = 0;
    const id = setInterval(() => {
      t = (t + MOCK_TRACKING_CLIENT_STEP) % 1;
      const pos = lerpLatLng(pickup, delivery, t);
      setMock({
        lat: pos.lat,
        lng: pos.lng,
        heading: bearingDegrees(pickup, delivery),
        speed: 8,
        updatedAt: new Date().toISOString(),
      });
    }, MOCK_TRACKING_CLIENT_INTERVAL_MS);
    return () => clearInterval(id);
  }, [
    enabled,
    order.pickupLat,
    order.pickupLng,
    order.deliveryLat,
    order.deliveryLng,
  ]);

  return mock;
}

export default function OrderTrackingMap({ order }: OrderTrackingMapProps) {
  const { mode, setMode, onMapError, mapFailed } = useTrackingMode();
  const vehicleType = resolveTrackingVehicleType(order.vehicle?.type);
  const showToast = useUIStore((s) => s.showToast);
  const useClientMock = USE_MOCK_TRACKING_CLIENT;
  const mockSnapshot = useMockLocation(order, useClientMock);
  const locationSource = useClientMock ? mockSnapshot : order.driverLocation;
  const { position, bearing, isStale, lastUpdated } = useAnimatedPosition(
    locationSource,
    useClientMock
      ? {
          animationMs: MOCK_TRACKING_CLIENT_ANIMATION_MS,
          enablePrediction: false,
        }
      : undefined,
  );

  const showMap = ["picked_up", "in_transit"].includes(order.status);
  const pickupOk = hasCoords(order.pickupLat, order.pickupLng);
  const deliveryOk = hasCoords(order.deliveryLat, order.deliveryLng);

  useEffect(() => {
    if (mapFailed) {
      showToast("Map unavailable — using route view", "info");
    }
  }, [mapFailed, showToast]);

  const pickup = useMemo(
    () => (pickupOk ? { lat: order.pickupLat!, lng: order.pickupLng! } : null),
    [pickupOk, order.pickupLat, order.pickupLng],
  );
  const delivery = useMemo(
    () =>
      deliveryOk ? { lat: order.deliveryLat!, lng: order.deliveryLng! } : null,
    [deliveryOk, order.deliveryLat, order.deliveryLng],
  );

  if (!showMap || !pickup || !delivery) return null;

  const waitingForDriver = !position && !useClientMock;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 mb-6 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-bold text-slate-800">
          Live delivery map
        </h2>
        <TrackingModeToggle mode={mode} onChange={setMode} />
      </div>

      {mode === "map" ? (
        <Suspense
          fallback={
            <div className="h-[360px] rounded-2xl bg-slate-50 flex items-center justify-center">
              <span className="material-symbols-outlined text-3xl text-primary animate-spin">
                progress_activity
              </span>
            </div>
          }
        >
          <LiveTrackingMap
            pickup={pickup}
            delivery={delivery}
            driverPosition={position}
            bearing={bearing}
            vehicleType={vehicleType}
            isStale={isStale}
            lastUpdated={lastUpdated}
            onError={onMapError}
            waitingForDriver={waitingForDriver}
          />
        </Suspense>
      ) : (
        <SimpleRouteTracker
          pickup={pickup}
          delivery={delivery}
          driverPosition={position}
          bearing={bearing}
          vehicleType={vehicleType}
          isStale={isStale}
          lastUpdated={lastUpdated}
          pickupLabel={order.pickupAddress}
          deliveryLabel={order.deliveryAddress}
          waitingForDriver={waitingForDriver}
        />
      )}
    </div>
  );
}
